import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

// Le serveur ne renvoie aucune info de progression réelle pendant la génération du
// PDF/Excel (une seule requête HTTP synchrone) : on simule une progression qui ralentit
// en approchant 90%, calibrée sur les temps observés en prod (~45s-90s). Elle ne prétend
// jamais atteindre 100% avant que la vraie réponse arrive.
const APPROACH_TAU_MS = 45000;
const SIMULATED_CAP = 90;

const STEPS = [
	{ until: 30, label: 'Récupération des données' },
	{ until: 75, label: 'Mise en forme du document' },
	{ until: Infinity, label: 'Finalisation du fichier' },
];

const fadeIn = keyframes`
	from { opacity: 0; transform: translateY(8px) scale(0.98); }
	to { opacity: 1; transform: translateY(0) scale(1); }
`;

const shimmer = keyframes`
	from { transform: translateX(-100%); }
	to { transform: translateX(100%); }
`;

const pulse = keyframes`
	0%, 100% { box-shadow: 0 0 0 0 rgba(var(--ui-primary-rgb), 0.35); }
	50% { box-shadow: 0 0 0 10px rgba(var(--ui-primary-rgb), 0); }
`;

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	background: rgba(var(--ui-dark-rgb), 0.45);
	backdrop-filter: blur(3px);
	-webkit-backdrop-filter: blur(3px);
`;

const Card = styled.div`
	width: min(100%, 380px);
	padding: 28px 28px 24px;
	border-radius: 16px;
	background: rgb(var(--ui-white-rgb));
	box-shadow: 0 24px 60px rgba(var(--ui-black-rgb), 0.25);
	font-family: 'Inter', sans-serif;
	text-align: center;
	animation: ${fadeIn} 220ms ease-out both;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

const IconBadge = styled.div`
	width: 56px;
	height: 56px;
	margin: 0 auto 16px;
	display: grid;
	place-items: center;
	border-radius: 50%;
	color: rgb(var(--ui-primary-rgb));
	background: rgba(var(--ui-primary-rgb), 0.12);
	animation: ${pulse} 2s ease-out infinite;
`;

const Title = styled.h3`
	margin: 0 0 4px;
	font-size: 1.05rem;
	font-weight: 600;
	color: rgb(var(--ui-dark-rgb));
`;

const Step = styled.p`
	margin: 0 0 20px;
	min-height: 1.3em;
	font-size: 0.875rem;
	color: rgb(var(--ui-gray-rgb));
`;

const Track = styled.div`
	position: relative;
	height: 8px;
	overflow: hidden;
	border-radius: 999px;
	background: rgba(var(--ui-tertiary-rgb), 0.14);
`;

const Fill = styled.div`
	position: relative;
	height: 100%;
	overflow: hidden;
	border-radius: inherit;
	background: linear-gradient(
		90deg,
		rgb(var(--ui-tertiary-rgb)),
		rgb(var(--ui-primary-rgb))
	);
	transition: width 300ms linear;

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			90deg,
			transparent,
			rgba(var(--ui-white-rgb), 0.45),
			transparent
		);
		animation: ${shimmer} 1.6s ease-in-out infinite;
	}

	@media (prefers-reduced-motion: reduce) {
		&::after {
			animation: none;
			display: none;
		}
	}
`;

const Meta = styled.div`
	margin-top: 10px;
	display: flex;
	justify-content: space-between;
	font-size: 0.8rem;
	color: rgb(var(--ui-gray-rgb));
`;

const Percent = styled.span`
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	color: rgb(var(--ui-tertiary-shade-rgb));
`;

type ExportProgressLoaderProps = {
	isLoading: boolean;
};

export const ExportProgressLoader: React.FC<ExportProgressLoaderProps> = ({
	isLoading,
}) => {
	const [progress, setProgress] = useState(0);
	const startRef = useRef<number | null>(null);

	useEffect(() => {
		if (!isLoading) {
			setProgress(0);
			startRef.current = null;
			return;
		}

		startRef.current = Date.now();
		const interval = setInterval(() => {
			const elapsed = Date.now() - (startRef.current ?? Date.now());
			const pct = SIMULATED_CAP * (1 - Math.exp(-elapsed / APPROACH_TAU_MS));
			setProgress(pct);
		}, 300);

		return () => clearInterval(interval);
	}, [isLoading]);

	if (!isLoading) return null;

	const pct = Math.round(progress);
	const step = STEPS.find(s => progress < s.until) ?? STEPS[STEPS.length - 1];

	return (
		<Overlay role="alertdialog" aria-modal="true" aria-label="Génération du fichier">
			<Card>
				<IconBadge aria-hidden="true">
					<DescriptionOutlinedIcon sx={{ fontSize: 28 }} />
				</IconBadge>
				<Title>Génération du fichier</Title>
				<Step aria-live="polite">{step.label}…</Step>
				<Track
					role="progressbar"
					aria-valuemin={0}
					aria-valuemax={100}
					aria-valuenow={pct}
				>
					<Fill style={{ width: `${Math.max(pct, 3)}%` }} />
				</Track>
				<Meta>
					<span>Les gros volumes peuvent prendre une minute</span>
					<Percent>{pct}%</Percent>
				</Meta>
			</Card>
		</Overlay>
	);
};
